import { createEffect, createSignal, onCleanup, onMount, Show } from 'solid-js';
import { MinecraftRenderer } from '../renderer/MinecraftRenderer';
import { getMeshWorker, type MeshWorkerClient } from '../wasm/MeshWorkerClient';
import { createLiveViewClient, type LiveViewClient } from '../services/liveViewClient';

const DEG_TO_RAD = Math.PI / 180;
const EYE_HEIGHT = 1.62; // MC physics is feet-level; camera lives at eye height.

// Exponential smoothing time constants. 80ms at 20Hz sample rate: ~0.34-block lag at walk speed, ~250ms catch-up on direction changes.
const POS_SMOOTHING_MS = 80;
const ROT_SMOOTHING_MS = 40;

// Outbound SetLookDirection throttle — physics tick is 20Hz, anything faster is wasted bandwidth.
const LOOK_FLUSH_MS = 50;

type MovementKey = 'forward' | 'backward' | 'left' | 'right' | 'jump' | 'sneak';

type MovementState = Record<MovementKey, boolean>;

const EMPTY_MOVEMENT: MovementState = {
  forward: false,
  backward: false,
  left: false,
  right: false,
  jump: false,
  sneak: false,
};

export function LiveView(props: { botId: string }) {
  const [status, setStatus] = createSignal('Initializing...');
  const [error, setError] = createSignal<string | null>(null);
  const [movement, setMovement] = createSignal<MovementState>({ ...EMPTY_MOVEMENT });

  let canvasRef: HTMLCanvasElement | undefined;
  let renderer: MinecraftRenderer | null = null;
  let client: LiveViewClient | null = null;
  let worker: MeshWorkerClient | null = null;

  let localYaw = 0;
  let localPitch = 0;
  let lookInitialised = false;
  const MOUSE_SENSITIVITY = 0.15;

  type PoseSample = { X: number; Y: number; Z: number; Yaw: number; Pitch: number };
  let targetPos: PoseSample | null = null;
  let renderedX = 0;
  let renderedY = 0;
  let renderedZ = 0;
  let renderedYaw = 0;
  let renderedPitch = 0;
  let renderedPoseInitialised = false;
  let lastCameraFrameTime = 0;

  let lastLookSent = 0;
  let lastSentYaw = Number.NaN;
  let lastSentPitch = Number.NaN;

  let unsubscribeChunks: (() => void) | null = null;
  let unsubscribeChunkUnloads: (() => void) | null = null;
  let unsubscribeBlockBatch: (() => void) | null = null;
  let disposed = false;

  function teardown() {
    if (unsubscribeChunks) { try { unsubscribeChunks(); } catch { } unsubscribeChunks = null; }
    if (unsubscribeChunkUnloads) { try { unsubscribeChunkUnloads(); } catch { } unsubscribeChunkUnloads = null; }
    if (unsubscribeBlockBatch) { try { unsubscribeBlockBatch(); } catch { } unsubscribeBlockBatch = null; }
    if (renderer) { try { renderer.dispose(); } catch { } renderer = null; }
    if (client) { client.dispose().catch(() => { }); client = null; }
    worker = null;
    renderedPoseInitialised = false;
    lookInitialised = false;
  }

  async function boot() {
    try {
      setError(null);
      setStatus('Loading WASM runtime...');
      const w = getMeshWorker();
      w.onProgress((message) => {
        if (!disposed) setStatus(message);
      });
      const atlas = await w.init();
      if (disposed) return;
      worker = w;
      worker.clearWorld();

      if (!canvasRef) throw new Error('Canvas not mounted');

      setStatus('Starting renderer...');
      const mc = new MinecraftRenderer(canvasRef, atlas);
      mc.setTrackingExternal(true);
      renderer = mc;

      worker.onMesh((m) => {
        renderer?.addSectionMesh(m.cx, m.sy, m.cz, m.vertices, m.indices);
      });

      setStatus('Connecting to bot...');
      const c = createLiveViewClient(props.botId);
      client = c;

      unsubscribeChunks = c.onChunk((chunk) => {
        worker?.applyChunk(chunk.Cx, chunk.Cz, chunk.Blocks, chunk.Biomes);
      });

      unsubscribeChunkUnloads = c.onChunkUnload((u) => {
        worker?.unloadChunk(u.Cx, u.Cz);
        renderer?.removeChunkMesh(u.Cx, u.Cz);
      });

      unsubscribeBlockBatch = c.onBlockBatch((batch) => {
        worker?.setBlocks(
          batch.Updates.map((u) => ({
            wx: u.Wx,
            wy: u.Wy,
            wz: u.Wz,
            id: (u.Packed >> 4) & 0xfff,
            meta: u.Packed & 0xf,
          })),
        );
      });

      await c.connect();
      if (disposed) return;

      createEffect(() => {
        const pos = c.position();
        if (!pos) return;

        if (!lookInitialised) {
          localYaw = pos.Yaw;
          localPitch = pos.Pitch;
          lastSentYaw = pos.Yaw;
          lastSentPitch = pos.Pitch;
          lookInitialised = true;
        }

        targetPos = pos;
        worker?.setPlayerPosition(pos.X, pos.Y, pos.Z);

        if (!renderedPoseInitialised) {
          renderedX = pos.X;
          renderedY = pos.Y;
          renderedZ = pos.Z;
          renderedYaw = pos.Yaw;
          renderedPitch = pos.Pitch;
          renderedPoseInitialised = true;
        }
      });

      mc.start(cameraFrame);

      setStatus('');
    } catch (err) {
      console.error('LiveView init failed', err);
      setError(String(err));
      setStatus('');
    }
  }

  async function restart() {
    teardown();
    await boot();
  }

  onMount(() => { void boot(); });

  onCleanup(() => {
    disposed = true;
    if (worker) {
      worker.onMesh(() => {});
      worker.clearWorld();
    }
    if (unsubscribeChunks) {
      try { unsubscribeChunks(); } catch { }
    }
    if (unsubscribeChunkUnloads) {
      try { unsubscribeChunkUnloads(); } catch { }
    }
    if (unsubscribeBlockBatch) {
      try { unsubscribeBlockBatch(); } catch { }
    }
    if (renderer) {
      try { renderer.dispose(); } catch { }
      renderer = null;
    }
    if (client) {
      client.dispose().catch(() => { });
      client = null;
    }
  });

  function updateMovement(patch: Partial<MovementState>) {
    const prev = movement();
    let changed = false;
    const next: MovementState = { ...prev };
    for (const k of Object.keys(patch) as MovementKey[]) {
      const v = patch[k]!;
      if (next[k] !== v) {
        next[k] = v;
        changed = true;
      }
    }
    if (!changed) return;
    setMovement(next);
    if (client) {
      client.setMovementInput(next).catch((e) => {
        console.warn('[LiveView] setMovementInput failed', e);
      });
    }
  }

  function releaseAllMovement() {
    updateMovement({ ...EMPTY_MOVEMENT });
  }

  function movementKeyFromEvent(ev: KeyboardEvent): MovementKey | null {
    switch (ev.code) {
      case 'KeyW': case 'ArrowUp':    return 'forward';
      case 'KeyS': case 'ArrowDown':  return 'backward';
      case 'KeyA': case 'ArrowLeft':  return 'left';
      case 'KeyD': case 'ArrowRight': return 'right';
      case 'Space':                   return 'jump';
      case 'ShiftLeft': case 'ShiftRight': return 'sneak';
      default: return null;
    }
  }

  function isTypingTarget(target: EventTarget | null): boolean {
    if (!(target instanceof HTMLElement)) return false;
    const tag = target.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
    return target.isContentEditable;
  }

  function handleKeyDown(ev: KeyboardEvent) {
    if (isTypingTarget(ev.target)) return;
    const key = movementKeyFromEvent(ev);
    if (!key) return;
    if (ev.repeat) {
      ev.preventDefault();
      return;
    }
    ev.preventDefault();
    updateMovement({ [key]: true } as Partial<MovementState>);
  }

  function handleKeyUp(ev: KeyboardEvent) {
    if (isTypingTarget(ev.target)) return;
    const key = movementKeyFromEvent(ev);
    if (!key) return;
    ev.preventDefault();
    updateMovement({ [key]: false } as Partial<MovementState>);
  }

  function handleWindowBlur() {
    // Tab blur never fires keyup — drop held inputs so the bot doesn't keep walking.
    releaseAllMovement();
  }

  function handleCanvasClick() {
    canvasRef?.requestPointerLock();
  }

  function handleMouseMove(ev: MouseEvent) {
    if (document.pointerLockElement !== canvasRef) return;
    localYaw += ev.movementX * MOUSE_SENSITIVITY;
    localYaw = ((localYaw + 180) % 360 + 360) % 360 - 180;
    // localPitch tracks Minecraft convention (positive = looking down, negative = looking up).
    // Mouse-down (positive movementY) → operator wants to look down → increment pitch.
    localPitch = Math.max(-90, Math.min(90, localPitch + ev.movementY * MOUSE_SENSITIVITY));
  }

  function handlePointerLockChange() {
    if (document.pointerLockElement !== canvasRef) {
      if (targetPos) {
        localYaw = targetPos.Yaw;
        localPitch = targetPos.Pitch;
      }
    }
  }

  // Single render-loop callback: renderedPos += (target - renderedPos) * (1 - exp(-dt/tau)) — frame-independent first-order low-pass.
  function cameraFrame(now: number) {
    if (disposed || !renderer || !targetPos || !renderedPoseInitialised) {
      lastCameraFrameTime = now;
      return;
    }

    const dtMs = Math.max(0, now - lastCameraFrameTime);
    lastCameraFrameTime = now;

    const posK = 1 - Math.exp(-dtMs / POS_SMOOTHING_MS);
    renderedX += (targetPos.X - renderedX) * posK;
    renderedY += (targetPos.Y - renderedY) * posK;
    renderedZ += (targetPos.Z - renderedZ) * posK;

    const isLocked = document.pointerLockElement === canvasRef;
    let yaw: number;
    let pitch: number;
    if (isLocked) {
      yaw = localYaw;
      pitch = localPitch;
      renderedYaw = yaw;
      renderedPitch = pitch;
    } else {
      // Shortest-arc handling. Minecraft yaw is unbounded — a possessing operator's
      // accumulates past ±360 — so the delta must wrap with a modulo, not one ±360 step.
      const rotK = 1 - Math.exp(-dtMs / ROT_SMOOTHING_MS);
      const deltaYaw = (((targetPos.Yaw - renderedYaw) % 360) + 540) % 360 - 180;
      renderedYaw += deltaYaw * rotK;
      if (renderedYaw > 180) renderedYaw -= 360;
      if (renderedYaw < -180) renderedYaw += 360;

      renderedPitch += (targetPos.Pitch - renderedPitch) * rotK;

      yaw = renderedYaw;
      pitch = renderedPitch;
      localYaw = yaw;
      localPitch = pitch;
    }

    const yawRad = -yaw * DEG_TO_RAD + Math.PI;
    // FirstPersonCamera uses Math.sin(pitch) for the Y look component (positive = look up).
    // MC convention is opposite (positive = look down), so flip the sign at the render boundary.
    const pitchRad = -pitch * DEG_TO_RAD;
    renderer.setExternalPose(renderedX, renderedY + EYE_HEIGHT, renderedZ, yawRad, pitchRad);

    if (isLocked && client && now - lastLookSent >= LOOK_FLUSH_MS) {
      if (localYaw !== lastSentYaw || localPitch !== lastSentPitch) {
        lastSentYaw = localYaw;
        lastSentPitch = localPitch;
        lastLookSent = now;
        client.setLookDirection(localYaw, localPitch).catch(() => { });
      }
    }
  }

  onMount(() => {
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    window.addEventListener('blur', handleWindowBlur);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('pointerlockchange', handlePointerLockChange);
  });

  onCleanup(() => {
    window.removeEventListener('keydown', handleKeyDown);
    window.removeEventListener('keyup', handleKeyUp);
    window.removeEventListener('blur', handleWindowBlur);
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('pointerlockchange', handlePointerLockChange);
    if (document.pointerLockElement === canvasRef) document.exitPointerLock();
    if (client) {
      client.setMovementInput({ ...EMPTY_MOVEMENT }).catch(() => { });
    }
  });

  return (
    <div style={{
      position: 'relative',
      width: '100%',
      height: '480px',
      'background-color': '#0b0e14',
      'border-radius': '6px',
      overflow: 'hidden',
    }}>
      <canvas
        ref={(el) => (canvasRef = el)}
        style={{ width: '100%', height: '100%', display: 'block', cursor: 'pointer' }}
        onClick={handleCanvasClick}
      />

      <div style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        width: '14px',
        height: '14px',
        transform: 'translate(-50%, -50%)',
        'pointer-events': 'none',
        'mix-blend-mode': 'difference',
      }}>
        <div style={{
          position: 'absolute',
          top: '50%',
          left: 0,
          width: '100%',
          height: '2px',
          transform: 'translateY(-50%)',
          'background-color': '#fff',
        }} />
        <div style={{
          position: 'absolute',
          left: '50%',
          top: 0,
          width: '2px',
          height: '100%',
          transform: 'translateX(-50%)',
          'background-color': '#fff',
        }} />
      </div>

      <div style={{
        position: 'absolute',
        bottom: '8px',
        left: '8px',
        display: 'flex',
        gap: '12px',
        padding: '8px',
        'background-color': 'rgba(11, 14, 20, 0.75)',
        'border-radius': '6px',
        'backdrop-filter': 'blur(6px)',
        'font-size': '11px',
        'font-weight': '600',
        'user-select': 'none',
        'touch-action': 'none',
      }}>
        <div style={{
          display: 'grid',
          'grid-template-columns': 'repeat(3, 34px)',
          'grid-template-rows': 'repeat(3, 34px)',
          gap: '4px',
        }}>
          <div /> <MoveButton label="W" active={movement().forward}  onChange={(v) => updateMovement({ forward: v })}  /> <div />
          <MoveButton label="A" active={movement().left}     onChange={(v) => updateMovement({ left: v })}     />
          <div />
          <MoveButton label="D" active={movement().right}    onChange={(v) => updateMovement({ right: v })}    />
          <div /> <MoveButton label="S" active={movement().backward} onChange={(v) => updateMovement({ backward: v })} /> <div />
        </div>

        <div style={{
          display: 'flex',
          'flex-direction': 'column',
          gap: '4px',
          'justify-content': 'center',
        }}>
          <MoveButton label="Jump"  active={movement().jump}  onChange={(v) => updateMovement({ jump: v })}  wide />
          <MoveButton label="Sneak" active={movement().sneak} onChange={(v) => updateMovement({ sneak: v })} wide />
        </div>
      </div>

      <Show when={status() || error()}>
        <div style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          'flex-direction': 'column',
          gap: '12px',
          'align-items': 'center',
          'justify-content': 'center',
          color: error() ? '#ff6b6b' : '#9ca3af',
          'font-size': '14px',
          padding: '0 24px',
          'text-align': 'center',
        }}>
          <div style={{ 'pointer-events': 'none' }}>{error() ?? status()}</div>
          <Show when={error()}>
            <button type="button" class="btn btn-secondary btn-sm" onClick={() => void restart()}>
              <i class="fa-solid fa-rotate" /> Retry
            </button>
          </Show>
        </div>
      </Show>
    </div>
  );
}

// Pointer-capture: a drag off the button still fires release — otherwise pressing W + sliding off leaves the bot walking.
function MoveButton(props: {
  label: string;
  active: boolean;
  onChange: (pressed: boolean) => void;
  wide?: boolean;
}) {
  function press(ev: PointerEvent) {
    ev.preventDefault();
    const el = ev.currentTarget as HTMLButtonElement;
    try { el.setPointerCapture(ev.pointerId); } catch { }
    props.onChange(true);
  }

  function release(ev: PointerEvent) {
    ev.preventDefault();
    const el = ev.currentTarget as HTMLButtonElement;
    try { el.releasePointerCapture(ev.pointerId); } catch { }
    props.onChange(false);
  }

  return (
    <button
      type="button"
      onPointerDown={press}
      onPointerUp={release}
      onPointerCancel={release}
      onPointerLeave={(ev) => { if (props.active) release(ev); }}
      onContextMenu={(ev) => ev.preventDefault()}
      style={{
        width: props.wide ? '72px' : '34px',
        height: '34px',
        'border-radius': '4px',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        cursor: 'pointer',
        'background-color': props.active ? 'var(--accent-primary, #7c4dff)' : 'transparent',
        color: props.active ? '#fff' : '#9ca3af',
        'font-size': '11px',
        'font-weight': '600',
        'font-family': 'inherit',
        'touch-action': 'none',
      }}
    >
      {props.label}
    </button>
  );
}
